import React, { useState, useEffect, useCallback } from 'react';
import FeeSettings from '../components/settings/FeeSettings';
import './PaymentsPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const fmtTZS = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 1_000_000) return `TZS ${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000)     return `TZS ${(num / 1_000).toFixed(0)}K`;
  return `TZS ${num.toLocaleString()}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-GB');
  } catch {
    return String(d);
  }
};

const PAGE_SIZE = 20;

const PaymentsPage = () => {
  const [tab, setTab] = useState('live'); // 'live' | 'internal'
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  // ── Live (ClickPesa) tab ──────────────────────────────────────────
  const [live, setLive] = useState({ data: [], totalCount: 0 });
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [skip, setSkip] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const loadLive = useCallback(async () => {
    setLiveLoading(true); setLiveError(null);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE_SIZE), order_by: 'DESC' });
      if (statusFilter) params.set('status', statusFilter);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const res = await apiFetch(`/api/admin/payments?${params.toString()}`);
      setLive({ data: res.data || [], totalCount: res.totalCount || 0 });
    } catch (e) {
      setLiveError(e.message);
    } finally {
      setLiveLoading(false);
    }
  }, [statusFilter, startDate, endDate, skip]);

  useEffect(() => { if (tab === 'live') loadLive(); }, [tab, loadLive]);

  const syncPayments = async () => {
    setSyncing(true);
    try {
      const res = await apiFetch('/api/admin/payments/sync?limit=100', { method: 'POST' });
      if (res.skipped === 'quota') {
        showToast('Sync skipped — ClickPesa daily quota is exhausted.', 'error');
      } else {
        showToast(
          `Checked ${res.checked ?? 0} · Changed ${res.changed ?? 0} · Settled ${res.settled ?? 0} · Failed ${res.failed ?? 0} · Waiting ${res.waiting ?? 0}`
        );
      }
      loadLive();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  // ── Internal Records tab ──────────────────────────────────────────
  const [txs, setTxs] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [txError, setTxError] = useState(null);
  const [txSearch, setTxSearch] = useState('');

  const loadTransactions = useCallback(async () => {
    setTxLoading(true); setTxError(null);
    try {
      const res = await apiFetch('/api/admin/transactions');
      setTxs(res.transactions || []);
    } catch (e) {
      setTxError(e.message);
    } finally {
      setTxLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 'internal') loadTransactions(); }, [tab, loadTransactions]);

  const filteredTxs = txSearch.trim()
    ? txs.filter((t) => {
        const q = txSearch.trim().toLowerCase();
        return [t.orderId, t.sellerId, t.status, t.clickpesaReference]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
    : txs;

  const switchTab = (next) => { setTab(next); };

  return (
    <div className="pay-page">
      {toast && <div className={`pay-toast pay-toast--${toast.type}`}>{toast.msg}</div>}

      <div className="pay-header">
        <div>
          <h1 className="dashboard-title">Payments</h1>
          <p className="dashboard-subtitle">Live ClickPesa payments and the internal transaction ledger.</p>
        </div>
        {tab === 'live' && (
          <button className="pay-refresh" onClick={syncPayments} disabled={syncing}>
            {syncing ? '…' : '⟳ Sync Payments'}
          </button>
        )}
      </div>

      <div className="pay-tabs">
        <button className={`pay-tab ${tab === 'live' ? 'active' : ''}`} onClick={() => switchTab('live')}>
          Live (ClickPesa)
        </button>
        <button className={`pay-tab ${tab === 'internal' ? 'active' : ''}`} onClick={() => switchTab('internal')}>
          Internal Records
        </button>
        <button className={`pay-tab ${tab === 'fees' ? 'active' : ''}`} onClick={() => switchTab('fees')}>
          Fees &amp; Charges
        </button>
      </div>

      {/* What Beetle earns, next to the payments it earned it from. */}
      {tab === 'fees' && <FeeSettings onToast={showToast} />}

      {tab === 'live' && (
        <>
          <div className="pay-filters">
            <select className="pay-select" value={statusFilter} onChange={(e) => { setSkip(0); setStatusFilter(e.target.value); }}>
              <option value="">All statuses</option>
              <option value="PROCESSING">Processing</option>
              <option value="PENDING">Pending</option>
              <option value="SUCCESS">Success</option>
              <option value="SETTLED">Settled</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
              <option value="REVERSED">Reversed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <input
              className="pay-date-input"
              type="date"
              value={startDate}
              onChange={(e) => { setSkip(0); setStartDate(e.target.value); }}
              title="Start date"
            />
            <input
              className="pay-date-input"
              type="date"
              value={endDate}
              onChange={(e) => { setSkip(0); setEndDate(e.target.value); }}
              title="End date"
            />
            <button className="pay-refresh" onClick={loadLive} disabled={liveLoading}>
              {liveLoading ? '…' : '↻ Refresh'}
            </button>
          </div>

          {liveError && (
            <div className="pay-error">{liveError} <button onClick={loadLive}>Retry</button></div>
          )}

          {!liveError && (
            <div className="pay-table-wrap">
              <table className="pay-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Amount</th>
                    <th>Currency</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {live.data.length === 0 && !liveLoading && (
                    <tr><td colSpan={6} className="pay-empty-cell">No payments found for this filter.</td></tr>
                  )}
                  {live.data.map((p, i) => (
                    <tr key={p.id || p.orderReference || i}>
                      <td><code className="pay-code">{p.orderReference || p.id || '—'}</code></td>
                      <td className="pay-amount">{fmtTZS(p.amount ?? p.collectedAmount)}</td>
                      <td>{p.currency || 'TZS'}</td>
                      <td>{p.channel || p.paymentMethod || '—'}</td>
                      <td>
                        <span className={`pay-chip pay-chip--${String(p.status || '').toLowerCase()}`}>
                          {p.status || '—'}
                        </span>
                      </td>
                      <td>{fmtDate(p.createdAt || p.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="pay-pagination">
            <button
              className="pay-btn pay-btn--ghost pay-btn--sm"
              onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))}
              disabled={skip === 0 || liveLoading}
            >
              ← Prev
            </button>
            <span className="pay-pagination-info">
              {live.totalCount > 0
                ? `${skip + 1}–${Math.min(skip + PAGE_SIZE, live.totalCount)} of ${live.totalCount}`
                : liveLoading ? 'Loading…' : '0 of 0'}
            </span>
            <button
              className="pay-btn pay-btn--ghost pay-btn--sm"
              onClick={() => setSkip((s) => s + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= live.totalCount || liveLoading}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {tab === 'internal' && (
        <>
          <div className="pay-filters">
            <input
              className="pay-search"
              type="text"
              placeholder="Search by order ID, seller ID, status, reference…"
              value={txSearch}
              onChange={(e) => setTxSearch(e.target.value)}
            />
            <button className="pay-refresh" onClick={loadTransactions} disabled={txLoading}>
              {txLoading ? '…' : '↻ Refresh'}
            </button>
          </div>

          {txError && (
            <div className="pay-error">{txError} <button onClick={loadTransactions}>Retry</button></div>
          )}

          {!txError && (
            <div className="pay-table-wrap">
              <table className="pay-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Seller</th>
                    <th>Status</th>
                    <th>Escrow</th>
                    <th>Customer Total</th>
                    <th>Seller Net</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxs.length === 0 && !txLoading && (
                    <tr><td colSpan={7} className="pay-empty-cell">
                      {txs.length === 0 ? 'No transaction records.' : 'No records match this search.'}
                    </td></tr>
                  )}
                  {filteredTxs.map((t) => (
                    <tr key={t.$id || t.transactionId}>
                      <td><code className="pay-code">{t.orderId || '—'}</code></td>
                      <td><code className="pay-code">{(t.sellerId || '').slice(0, 12)}…</code></td>
                      <td>
                        <span className={`pay-chip pay-chip--${String(t.status || '').toLowerCase()}`}>
                          {t.status || '—'}
                        </span>
                      </td>
                      <td>{t.escrowState || '—'}</td>
                      <td className="pay-amount">{fmtTZS(t.customerTotal)}</td>
                      <td className="pay-amount">{fmtTZS(t.sellerNet)}</td>
                      <td>{fmtDate(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="pay-note">Showing the most recent 100 internal records (not paginated by the backend).</p>
        </>
      )}

      {tab !== 'fees' && (tab === 'live' ? liveLoading : txLoading) && (
        <div className="pay-loading"><div className="mp-spinner" /> Loading…</div>
      )}
    </div>
  );
};

export default PaymentsPage;
